
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import FormAnalyticsTracker from '../utils/analyticsTracker';
import API_BASE_URL from '../config/api';
import { evaluateStepConditions } from '../components/StepConditionEditor';
import { evaluateDisqualification } from '../components/DisqualificationEditor';

// ============ Theme Helpers ============
const THEME_DEFAULTS = {
  backgroundColor: '#f9fafb',
  formBackgroundColor: '#ffffff',
  primaryColor: '#111827',
  textColor: '#111827',
  labelColor: '#111827',
  inputBorderColor: '#e5e7eb',
  inputBorderRadius: '0',
  buttonBorderRadius: '8',
  buttonTextColor: '#ffffff',
  fontFamily: '',
  headingFontFamily: '',
  fontSize: '16',
  fieldSpacing: '24',
  formWidth: '100',
  formMaxWidth: '672',
  formPadding: '24',
  layout: 'single',
};

/**
 * Resolves formWidth for backward compatibility.
 * Legacy values are pixel numbers (e.g. '672'), new values are percentages (e.g. '100').
 * If the value is > 100, it's a legacy pixel value — treat as 100% width with that value as maxWidth.
 */
function resolveFormWidth(theme) {
  const rawWidth = getThemeVal(theme, 'formWidth');
  const rawMaxWidth = theme?.formMaxWidth || null;
  const numWidth = parseInt(rawWidth);

  // Legacy pixel value (> 100 means it's px, not %)
  if (numWidth > 100) {
    return { width: '100%', maxWidth: `${numWidth}px` };
  }

  const widthPercent = `${numWidth || 100}%`;
  const maxWidth = rawMaxWidth && rawMaxWidth !== 'none'
    ? `${rawMaxWidth}px`
    : 'none';

  return { width: widthPercent, maxWidth };
}

function getThemeVal(theme, key) {
  return theme?.[key] || THEME_DEFAULTS[key];
}

function getThemeFontFamily(fontFamily) {
  if (!fontFamily || fontFamily === 'System Default') {
    return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  }
  return `"${fontFamily}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
}

function getThemeGoogleFontsUrl(theme) {
  const fonts = [];
  if (theme?.fontFamily && theme.fontFamily !== 'System Default') {
    fonts.push(theme.fontFamily);
  }
  if (theme?.headingFontFamily && theme.headingFontFamily !== 'System Default' && theme.headingFontFamily !== theme.fontFamily) {
    fonts.push(theme.headingFontFamily);
  }
  if (fonts.length === 0) return null;
  const families = fonts.map(f => `family=${f.replace(/\s+/g, '+')}:wght@300;400;500;600;700`).join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

// Common country codes with flag emojis
const COUNTRY_CODES = [
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+45', country: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: '+46', country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+351', country: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: '+32', country: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: '+43', country: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: '+48', country: 'PL', name: 'Poland', flag: '🇵🇱' },
];

// Function to detect user's country based on IP
async function detectUserCountry() {
  try {
    const response = await fetch('https://free.freeipapi.com/api/json');
    const data = await response.json();
    
    if (data.countryCode) {
      // Find matching country in our COUNTRY_CODES array
      const matchedCountry = COUNTRY_CODES.find(country => country.country === data.countryCode);
      if (matchedCountry) {
        return matchedCountry;
      }
      
      // Fallback: if phone codes are provided, try to match by phone code
      if (data.phoneCodes && data.phoneCodes.length > 0) {
        const phoneCode = `+${data.phoneCodes[0]}`;
        const phoneMatchedCountry = COUNTRY_CODES.find(country => country.code === phoneCode);
        if (phoneMatchedCountry) {
          return phoneMatchedCountry;
        }
      }
    }
  } catch (error) {
    console.warn('Failed to detect user country from IP:', error);
  }
  
  // Default fallback to Denmark
  return COUNTRY_CODES.find(country => country.country === 'DK') || COUNTRY_CODES[9];
}

// Function to load and initialize Meta Pixel
function loadMetaPixel(pixelId) {
  if (!pixelId || typeof window === 'undefined') return;
  
  // Check if fbq is already loaded
  if (window.fbq) {
    console.log('Meta Pixel already loaded, initializing with ID:', pixelId);
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    return;
  }
  
  // Load Meta Pixel code
  (function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=true;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=true;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)})(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  
  console.log('Meta Pixel loaded and initialized with ID:', pixelId);
  
  // Add noscript img tag for users without JavaScript
  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
}

// Function to fire a custom Meta Pixel event
function fireMetaPixelEvent(eventName) {
  if (!eventName || typeof window === 'undefined' || !window.fbq) return;
  
  console.log('Firing Meta Pixel event:', eventName);
  window.fbq('track', eventName);
}

// Utility function to get URL parameters, including from parent window if in iframe
function getUrlParams() {
  try {
    // First, try to get params from current window
    let searchParams = new URLSearchParams(window.location.search);
    
    // If we're in an iframe, try to get params from parent window
    if (window.parent !== window) {
      try {
        const parentSearch = window.parent.location.search;
        if (parentSearch) {
          searchParams = new URLSearchParams(parentSearch);
        }
      } catch (e) {
        // Cross-origin iframe - parent access denied, use current window params
        console.log('Cannot access parent window URL (cross-origin), using current window params');
      }
    }
    
    // Convert to plain object
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  } catch (error) {
    console.warn('Error getting URL parameters:', error);
    return {};
  }
}

/**
 * Validates a pre-filled value for typed fields.
 * Returns the (possibly coerced) value on success, or null to silently drop it.
 */
function validatePrefilledValue(field, rawValue) {
  if (rawValue === undefined || rawValue === null) return null;
  const value = String(rawValue);

  switch (field.type) {
    case 'email': {
      // Basic RFC-5322-ish check
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRe.test(value) ? value : null;
    }
    case 'number': {
      if (value === '') return null;
      const num = Number(value);
      return isNaN(num) ? null : value;
    }
    case 'date': {
      // Must be a valid date string (YYYY-MM-DD for HTML date inputs)
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : value;
    }
    case 'select':
    case 'radio': {
      // Value must be one of the field's defined options
      const opts = field.options || [];
      return opts.includes(value) ? value : null;
    }
    case 'checkbox': {
      // Accept comma-separated list of valid options
      const opts = field.options || [];
      const chosen = value.split(',').map(v => v.trim()).filter(v => opts.includes(v));
      return chosen.length > 0 ? chosen : null;
    }
    case 'text':
    case 'textarea':
    case 'phone':
      return value;
    default:
      // heading, paragraph, image, hidden — not pre-fillable via slug
      return null;
  }
}

export default function PublicForm({ embed, noAnalytics }) {
  const { form_id } = useParams();
  const navigate = useNavigate();
  
  // Check URL parameters for noAnalytics flag
  const urlParams = new URLSearchParams(window.location.search);
  const disableAnalytics = noAnalytics || urlParams.get('noAnalytics') === 'true';
  
  const [form, setForm] = useState(null);
  const [versionId, setVersionId] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [stepHistory, setStepHistory] = useState([0]); // Track the path taken for Back navigation
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [phoneVerification, setPhoneVerification] = useState({}); // Track verification state for each phone field
  const [analyticsTracker, setAnalyticsTracker] = useState(null);
  const [hasStartedForm, setHasStartedForm] = useState(false);
  const [fingerprint, setFingerprint] = useState(null); // Store the generated fingerprint
  const [disqualifiedCondition, setDisqualifiedCondition] = useState(null); // Set when a disqualification condition matches
  const isSubmittingRef = useRef(false); // Guard against double-submit
  const formRef = useRef(null);
    
  const steps = useMemo(() => form?.fields?.steps || [], [form]);

  // Generate enhanced fingerprint on component mount
  useEffect(() => {
    const generateEnhancedFingerprint = async () => {
      try {
        console.log('Generating enhanced fingerprint with ThumbmarkJS...');
        const enhancedFingerprint = await getFingerprint();
        setFingerprint(enhancedFingerprint);
        console.log('Enhanced fingerprint generated:', enhancedFingerprint.substring(0, 16) + '...');
      } catch (error) {
        console.warn('Failed to generate enhanced fingerprint:', error);
        // Generate basic fingerprint as fallback
        const basicFingerprint = generateBasicFingerprint();
        setFingerprint(basicFingerprint);
        console.log('Using basic fingerprint as fallback');
      }
    };

    if (!disableAnalytics) {
      generateEnhancedFingerprint();
    }
  }, [disableAnalytics]); // Only run once on mount or when disableAnalytics changes

  // Basic fingerprint generation function (fallback)
  const generateBasicFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);
    
    const fingerprintData = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvas.toDataURL(),
      timestamp: Date.now()
    };
    
    return btoa(JSON.stringify(fingerprintData)).substring(0, 32);
  };
  const currStep = steps[stepIdx];

  const defaultTheme = "#11111aff";
  const formTheme = form?.settings?.theme || null;
  const hasCustomTheme = formTheme && Object.keys(formTheme).length > 0;
  const themeColor = form?.settings?.theme_color || (hasCustomTheme ? getThemeVal(formTheme, 'primaryColor') : defaultTheme);
  const buttonColor = form?.settings?.section_button_color || (hasCustomTheme ? getThemeVal(formTheme, 'primaryColor') : defaultTheme);

  // Derived theme values for rendering
  const themeStyles = useMemo(() => {
    if (!hasCustomTheme) return null;
    return {
      backgroundColor: getThemeVal(formTheme, 'backgroundColor'),
      formBackgroundColor: getThemeVal(formTheme, 'formBackgroundColor'),
      primaryColor: getThemeVal(formTheme, 'primaryColor'),
      textColor: getThemeVal(formTheme, 'textColor'),
      labelColor: getThemeVal(formTheme, 'labelColor'),
      inputBorderColor: getThemeVal(formTheme, 'inputBorderColor'),
      inputBorderRadius: `${getThemeVal(formTheme, 'inputBorderRadius')}px`,
      buttonBorderRadius: `${getThemeVal(formTheme, 'buttonBorderRadius')}px`,
      buttonTextColor: getThemeVal(formTheme, 'buttonTextColor'),
      fontFamily: getThemeFontFamily(formTheme?.fontFamily),
      headingFontFamily: getThemeFontFamily(formTheme?.headingFontFamily || formTheme?.fontFamily),
      fontSize: `${getThemeVal(formTheme, 'fontSize')}px`,
      fieldSpacing: `${getThemeVal(formTheme, 'fieldSpacing')}px`,
      ...resolveFormWidth(formTheme),
      formPadding: `${getThemeVal(formTheme, 'formPadding')}px`,
      layout: getThemeVal(formTheme, 'layout'),
      hasBoxBorder: parseInt(getThemeVal(formTheme, 'inputBorderRadius')) > 0,
    };
  }, [formTheme, hasCustomTheme]);


  useEffect(() => {
    fetch(`${API_BASE_URL}/forms/public/${form_id}`)
      .then(res => res.json())
      .then(data => {
        setForm(data);
        setVersionId(data.version_id); // Capture the version ID
        
        setLoading(false);
      })
      .catch(err => setLoading(false));
  }, [form_id]);

  // Initialize hidden field values and pre-filled values from URL parameters when form loads
  useEffect(() => {
    if (form && form.fields?.steps) {
      const urlParams = getUrlParams();
      const initialResponses = {};
      
      // Build a slug → field map across all steps (for pre-fill lookup)
      const slugToField = {};
      form.fields.steps.forEach(step => {
        (step.fields || []).forEach(field => {
          if (field.slug) {
            slugToField[field.slug] = field;
          }
        });
      });

      // Process all steps and fields
      form.fields.steps.forEach(step => {
        step.fields?.forEach(field => {
          if (field.type === 'hidden') {
            // Hidden fields: populated from a specific query param name or static default
            if (field.useQueryParam && field.queryParamName) {
              const paramValue = urlParams[field.queryParamName];
              if (paramValue !== undefined) {
                initialResponses[field.id] = decodeURIComponent(paramValue);
              }
            } else if (field.defaultValue) {
              initialResponses[field.id] = field.defaultValue;
            }
          } else if (field.slug && urlParams[field.slug] !== undefined) {
            // Visible fields: pre-fill from URL param matching the field's slug
            const rawValue = decodeURIComponent(urlParams[field.slug]);
            const validated = validatePrefilledValue(field, rawValue);
            if (validated !== null) {
              initialResponses[field.id] = validated;
            }
          }
        });
      });
      
      // Set initial responses if we found any
      if (Object.keys(initialResponses).length > 0) {
        setResponses(prev => ({ ...prev, ...initialResponses }));
      }
    }
  }, [form]);

  // Initialize analytics tracker when form is loaded
  useEffect(() => {
    if (form && form_id && !analyticsTracker && !disableAnalytics && fingerprint) {
      console.log('Initializing analytics tracker for form:', form_id);
      console.log('API_BASE_URL:', API_BASE_URL);
      console.log('Using fingerprint:', fingerprint.substring(0, 16) + '...');
      console.log('Form version ID:', versionId);
      
      const tracker = new FormAnalyticsTracker(form_id, API_BASE_URL, fingerprint, versionId);
      setAnalyticsTracker(tracker);
      
      // Setup form tracking when the form element is available
      setTimeout(() => {
        if (formRef.current) {
          console.log('Setting up form tracking on element:', formRef.current);
          tracker.setupFormTracking(formRef.current);
        }
      }, 100);
      
      // Cleanup on unmount
      return () => {
        console.log('Cleaning up analytics tracker');
      };
    } else if (disableAnalytics) {
      console.log('Analytics disabled for demo/test mode');
    } else if (!fingerprint) {
      console.log('Waiting for fingerprint generation before initializing analytics...');
    }
  }, [form, form_id, analyticsTracker, disableAnalytics, fingerprint, versionId]);

  // Load Google Fonts for custom theme
  useEffect(() => {
    if (hasCustomTheme) {
      const url = getThemeGoogleFontsUrl(formTheme);
      if (url) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.id = 'form-theme-fonts';
        // Remove existing if present
        const existing = document.getElementById('form-theme-fonts');
        if (existing) existing.remove();
        document.head.appendChild(link);
        return () => {
          const el = document.getElementById('form-theme-fonts');
          if (el) el.remove();
        };
      }
    }
  }, [formTheme, hasCustomTheme]);

  // Load Meta Pixel when form is loaded
  useEffect(() => {
    if (form && form.settings?.meta_pixel_id) {
      console.log('Loading Meta Pixel with ID:', form.settings.meta_pixel_id);
      loadMetaPixel(form.settings.meta_pixel_id);
    }
  }, [form]);

  // Track step changes
  useEffect(() => {
    if (analyticsTracker && currStep && stepIdx > 0) {
      console.log('Tracking step start for step:', stepIdx, currStep.id, currStep.title);
     
      // Track step start when moving to a new step
      analyticsTracker.handleStepStart(
        currStep.id, 
        stepIdx, 
        currStep.title
      );
    }
  }, [stepIdx, currStep, analyticsTracker]);

  // Auto-resize for embedded forms
  useEffect(() => {
    if (embed && window.parent !== window) {
      const sendHeight = () => {
        const height = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );
        
        window.parent.postMessage({
          type: 'resize',
          height: height + 20 // Add some padding
        }, '*');
      };

      // Send initial height
      sendHeight();

      // Setup resize observer for dynamic content changes
      const resizeObserver = new ResizeObserver(sendHeight);
      resizeObserver.observe(document.body);

      // Also listen for window resize
      window.addEventListener('resize', sendHeight);

      // Cleanup
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', sendHeight);
      };
    }
  }, [embed, stepIdx, loading]); // Re-run when step changes, form submits, or loading state changes

  // Check if current step is valid (without setting errors - for real-time button state)
  const isStepValid = () => {
    if (!currStep?.fields) return true;
    
    return currStep.fields.every(field => {
      // Check if required field is filled
      if (field.required && !responses[field.id]?.toString().trim()) {
        return false;
      }
      
      // Check phone verification requirement
      if (field.type === 'phone' && field.requireVerification) {
        const verification = phoneVerification[field.id];
        if (!verification?.verified) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Validation on step
  const validateStep = () => {
    let stepErrors = {};
    (currStep.fields || []).forEach(field => {
      if (field.required && !responses[field.id]?.toString().trim()) {
        stepErrors[field.id] = 'Required';
      }
      
      // Add phone verification error
      if (field.type === 'phone' && field.requireVerification) {
        const verification = phoneVerification[field.id];
        if (!verification?.verified) {
          stepErrors[field.id] = 'Phone verification required';
        }
      }
    });
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  /**
   * Resolve the next step index based on conditional routing.
   * Returns the index into the steps array, or -1 if the form should submit.
   */
  const resolveNextStep = useCallback(() => {
    if (!currStep) return stepIdx + 1;

    // Build a set of visited step IDs from the history to prevent routing cycles
    const visitedStepIds = new Set(stepHistory.map(idx => steps[idx]?.id).filter(Boolean));

    const { targetStepId, shouldSubmit } = evaluateStepConditions(currStep, responses, visitedStepIds);

    if (shouldSubmit) return -1; // Signal: submit the form

    if (targetStepId) {
      const targetIdx = steps.findIndex(s => s.id === targetStepId);
      if (targetIdx !== -1) return targetIdx;
    }

    // Default: next sequential step
    return stepIdx + 1;
  }, [currStep, responses, steps, stepIdx, stepHistory]);

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep()) {
      console.log('handleNext: Tracking step completion for:', currStep.id, stepIdx);

      const nextIdx = resolveNextStep();

      if (nextIdx === -1 || nextIdx >= steps.length) {
        // Condition routes to submit — trigger form submission
        handleSubmit(e);
        return;
      }

      setStepHistory(prev => [...prev, nextIdx]);
      setStepIdx(nextIdx);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    // Check if any phone field is verified - if so, prevent going back
    const hasVerifiedPhone = Object.values(phoneVerification).some(verification => verification.verified);
    if (hasVerifiedPhone) return;

    // Navigate back through the actual path the user took
    if (stepHistory.length > 1) {
      const newHistory = stepHistory.slice(0, -1);
      setStepHistory(newHistory);
      setStepIdx(newHistory[newHistory.length - 1]);
    } else {
      setStepIdx(0);
    }
  };

  // Determine if the current step is effectively the "last" step for this path.
  // This is true when: it's the actual last step in the array, OR the resolved next
  // step routes to submission or beyond the steps array.
  const isLastStep = useMemo(() => {
    if (stepIdx === steps.length - 1) return true;
    if (!currStep) return false;
    const nextIdx = resolveNextStep();
    return nextIdx === -1 || nextIdx >= steps.length;
  }, [stepIdx, steps.length, currStep, resolveNextStep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return; // Prevent double-submit
    if (!validateStep()) return;
    isSubmittingRef.current = true;
    
    try {
      // Track step completion for final step
      if (analyticsTracker && currStep) {
        analyticsTracker.handleStepComplete(
          currStep.id,
          stepIdx,
          currStep.title
        );
      }
      
      const res = await fetch(`${API_BASE_URL}/responses/forms/${form_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: responses,
          version_id: versionId 
        })
      });
      
      if (res.ok) {
        // Track form submission
        const resData = await res.json();
        console.log('Form submitted successfully:', resData);
        const r_id = resData.id || null;
        if (analyticsTracker) {
          analyticsTracker.handleFormSubmit(r_id);
        }

        // Fire custom Meta Pixel event if configured
        if (form.settings?.meta_pixel_event) {
          fireMetaPixelEvent(form.settings.meta_pixel_event);
        }

        // Evaluate disqualification conditions before normal redirect
        const disqualConditions = form.settings?.disqualification_conditions || [];
        const { matched, condition: matchedCondition } = evaluateDisqualification(disqualConditions, responses);
        if (matched && matchedCondition) {
          if (matchedCondition.message) {
            // Show a brief message before redirecting
            setDisqualifiedCondition(matchedCondition);
            setTimeout(() => {
              window.location.href = matchedCondition.redirect_url;
            }, 4000);
          } else {
            // Redirect immediately
            window.location.href = matchedCondition.redirect_url;
          }
          return;
        }

        // Handle redirect after submission
        if (form.settings?.redirect_url) {
          // Build redirect URL with field values if configured
          let redirectUrl = form.settings.redirect_url;
          
          // Check if there are fields to parse into the redirect URL
          const selectedFieldIds = form.settings?.redirect_hidden_fields || [];
          if (selectedFieldIds.length > 0) {
            // Get all fields from the form
            const allFields = (form.fields?.steps || []).flatMap(step => 
              (step.fields || [])
            );
            
            // Build query parameters from selected fields
            const params = new URLSearchParams();
            selectedFieldIds.forEach(fieldId => {
              const field = allFields.find(f => f.id === fieldId);
              // Check if field exists, has a slug, and has a value (including falsy values like 0 or empty string)
              if (field && field.slug && fieldId in responses) {
                params.append(field.slug, responses[fieldId]);
              }
            });
            
            // Append parameters to redirect URL
            const paramString = params.toString();
            if (paramString) {
              const separator = redirectUrl.includes('?') ? '&' : '?';
              redirectUrl = `${redirectUrl}${separator}${paramString}`;
            }
          }
          
          // External redirect - add small delay to ensure analytics tracking completes
          console.log('Redirecting to custom URL:', redirectUrl);
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 300); // 300ms delay to ensure tracking requests complete
        } else {
          // Default: redirect to internal thank you page
          const thankYouUrl = `/thank-you/${form_id}${r_id ? `?response_id=${r_id}` : ''}`;
          navigate(thankYouUrl);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      isSubmittingRef.current = false; // Allow retry on error
    }
  };

  const handleChange = (field, value) => {
    setResponses(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    
    // Track form start on first interaction
    if (!hasStartedForm && analyticsTracker && currStep) {
      console.log('handleChange: Tracking form start for field:', field);
      setHasStartedForm(true);
      analyticsTracker.handleFormStart(
        currStep.id,
        stepIdx,
        field
      );
      
      // Also track first step start
      if (stepIdx === 0) {
        console.log('handleChange: Also tracking first step start');
        analyticsTracker.handleStepStart(
          currStep.id,
          stepIdx,
          currStep.title
        );
      }
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-lg text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  if (!form)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Form not found</h2>
          <p className="text-gray-600">The form you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  if (!currStep)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No questions available</h2>
          <p className="text-gray-600">This form doesn't have any questions configured.</p>
        </div>
      </div>
    );

  // Disqualification screen — shown when a condition matched and there is a message to display
  if (disqualifiedCondition) {
    const rootBgDq = themeStyles?.backgroundColor || (embed ? 'transparent' : undefined);
    const rootFontDq = themeStyles?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${!themeStyles ? (embed ? 'bg-white' : 'bg-gray-50') : ''}`}
        style={{ fontFamily: rootFontDq, backgroundColor: rootBgDq, color: themeStyles?.textColor }}
      >
        <div
          className="text-center max-w-lg mx-auto px-6 py-12"
          style={themeStyles ? {
            backgroundColor: themeStyles.formBackgroundColor,
            borderRadius: `${Math.max(parseInt(getThemeVal(formTheme, 'inputBorderRadius')) || 0, 12)}px`,
            padding: themeStyles.formPadding,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          } : undefined}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: themeStyles?.primaryColor ? `${themeStyles.primaryColor}20` : '#f3f4f6' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8"
              viewBox="0 0 24 24"
              fill="none"
              stroke={themeStyles?.primaryColor || '#6b7280'}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p
            className={!themeStyles ? 'text-lg text-gray-700 leading-relaxed mb-6' : 'leading-relaxed mb-6'}
            style={themeStyles ? { fontSize: themeStyles.fontSize, color: themeStyles.textColor } : undefined}
          >
            {disqualifiedCondition.message}
          </p>
          <p
            className={!themeStyles ? 'text-sm text-gray-400' : 'text-sm'}
            style={themeStyles ? { color: '#9ca3af' } : undefined}
          >
            You will be redirected shortly...
          </p>
        </div>
      </div>
    );
  }
  
  // Determine root background and font
  const rootBg = themeStyles?.backgroundColor || (embed ? 'transparent' : undefined);
  const rootFont = themeStyles?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  const showFormCard = themeStyles && themeStyles.formBackgroundColor !== themeStyles.backgroundColor;

  return (
    <div 
      className={`min-h-screen form-container ${!themeStyles ? (embed ? 'bg-white' : 'bg-gray-50') : ''}`} 
      data-form-id={form_id}
      style={{ 
        fontFamily: rootFont,
        backgroundColor: rootBg,
        color: themeStyles?.textColor,
      }}
    >
      {/* Progress Bar - Fixed at top for non-embed */}
      {(steps.length > 1 && !embed) && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
          <ProgressBar stepIdx={Math.min(stepHistory.length - 1, steps.length - 1)} steps={steps.length} color={themeStyles?.primaryColor || themeColor} />
        </div>
      )}

      {/* Main Content Container */}
      <div className={`flex flex-col justify-center min-h-screen ${steps.length > 1 && !embed ? 'pt-16' : ''}`}>
        <div 
          className={`w-full mx-auto ${!themeStyles ? 'max-w-2xl px-6 py-8' : ''}`}
          style={themeStyles ? {
            width: themeStyles.width,
            maxWidth: themeStyles.maxWidth,
            padding: showFormCard ? '32px 16px' : '32px 24px',
          } : undefined}
        >
          {/* Form Card container when theme has different form background */}
          <div style={showFormCard ? {
            backgroundColor: themeStyles.formBackgroundColor,
            padding: themeStyles.formPadding,
            borderRadius: `${Math.max(parseInt(getThemeVal(formTheme, 'inputBorderRadius')) || 0, 12)}px`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
          } : undefined}>
          
          {/* Organization Logo - Show if enabled and available */}
          {form.settings?.show_org_logo && form.settings?.org_logo_url && (
            <div className="mb-12">
              <img 
                src={form.settings.org_logo_url} 
                alt={`${form.settings.org_name || 'Organization'} logo`}
                className="h-10 md:h-12 w-auto object-contain"
              />
            </div>
          )}
          
          {/* Form Header - Only show on first step or if embed */}
          {(stepIdx === 0 || embed) && !embed && form.settings?.show_form_title && (
            <div className="text-center mb-12">
              <h1 
                className={!themeStyles ? "text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight" : "font-bold mb-4 leading-tight"}
                style={themeStyles ? {
                  fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                  color: themeStyles.textColor,
                  fontFamily: themeStyles.headingFontFamily,
                } : undefined}
              >
                {form.title}
              </h1>
              {form.description && (
                <p 
                  className={!themeStyles ? "text-xl text-gray-600 leading-relaxed max-w-xl mx-auto" : "leading-relaxed max-w-xl mx-auto"}
                  style={themeStyles ? { fontSize: 'clamp(1rem, 2vw, 1.25rem)', color: '#6b7280' } : undefined}
                >
                  {form.description}
                </p>
              )}
            </div>
          )}

          {/* Form Content */}
          <form
            ref={formRef}
            className="space-y-8"
            autoComplete="off"
            onSubmit={isLastStep ? handleSubmit : handleNext}
            data-step-id={currStep?.id}
            data-step-index={stepIdx}
            data-step-title={currStep?.title}
          >
            {/* Step Content */}
            <div className="space-y-8">
              {/* Step Title */}
              {currStep?.title && (stepIdx > 0 || embed) && !form.settings?.hide_step_header_text && (
                <div className="text-center mb-8">
                  <h2 
                    className={!themeStyles ? "text-3xl md:text-4xl font-bold text-gray-900 leading-tight" : "font-bold leading-tight"}
                    style={themeStyles ? {
                      fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                      color: themeStyles.textColor,
                      fontFamily: themeStyles.headingFontFamily,
                    } : undefined}
                  >
                    {currStep.title}
                  </h2>
                </div>
              )}

              {/* Form Fields */}
              <div 
                className={!themeStyles ? "space-y-6" : ""}
                style={themeStyles ? (themeStyles.layout === 'two-column' ? {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: `${getThemeVal(formTheme, 'fieldSpacing')}px 24px`,
                } : {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: themeStyles.fieldSpacing,
                }) : undefined}
              >
                {(currStep?.fields || []).map(field => (
                  <FormField
                    key={field.id}
                    field={field}
                    value={responses[field.id] || (field.type === 'checkbox' ? [] : '')}
                    onChange={val => handleChange(field.id, val)}
                    error={errors[field.id]}
                    phoneVerification={phoneVerification}
                    setPhoneVerification={setPhoneVerification}
                    buttonColor={themeStyles?.primaryColor || buttonColor}
                    themeStyles={themeStyles}
                    isPrefilled={field.id in responses}
                  />
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-8">
              {stepHistory.length > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={Object.values(phoneVerification).some(verification => verification.verified)}
                  className={`px-8 py-4 text-base font-medium transition-all duration-200 ${
                    Object.values(phoneVerification).some(verification => verification.verified)
                      ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                      : 'text-gray-700 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'
                  }`}
                  style={{ borderRadius: themeStyles?.buttonBorderRadius || '8px' }}
                  data-step-action="back"
                >
                  {form.settings?.button_text_back || '\u2190 Back'}
                </button>
              )}
              <button
                type="submit"
                disabled={!isStepValid()}
                className={`flex-1 px-8 py-4 text-base font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-lg ${
                  isStepValid()
                    ? 'transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
                    : 'text-gray-400 bg-gray-600 cursor-not-allowed'
                }`}
                style={isStepValid() ? {
                  backgroundColor: themeStyles?.primaryColor || buttonColor,
                  color: themeStyles?.buttonTextColor || '#ffffff',
                  borderRadius: themeStyles?.buttonBorderRadius || '8px',
                  boxShadow: `0 4px 14px 0 ${themeStyles?.primaryColor || buttonColor}40`,
                }: { borderRadius: themeStyles?.buttonBorderRadius || '8px' }}
                data-step-action={isLastStep ? 'complete' : 'next'}
              >
                {isLastStep
                  ? (form.settings?.button_text_submit || '\u2713 Submit')
                  : (form.settings?.button_text_next || 'Continue \u2192')
                }
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ stepIdx, steps, color }) {
  return (
    <div className="w-full h-1 bg-gray-100">
      <div
        className="h-full transition-all duration-500 ease-out"
        style={{
          width: `${((stepIdx + 1) / steps) * 100}%`,
          background: color
        }}
      />
    </div>
  );
}

const PhoneField = React.memo(function PhoneField({ field, value, onChange, error, phoneVerification, setPhoneVerification }) {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[9]); // Default to Denmark
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countryDetected, setCountryDetected] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const buttonRef = React.useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = React.useRef(null);
  
  // Auto-detect country on component mount
  useEffect(() => {
    if (!countryDetected) {
      detectUserCountry().then(detectedCountry => {
        setSelectedCountry(detectedCountry);
        setCountryDetected(true);
      });
    }
  }, [countryDetected]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showCountryDropdown && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showCountryDropdown]);

  // Filter countries based on search query
  const filteredCountries = COUNTRY_CODES.filter(country => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      country.name.toLowerCase().includes(query) ||
      country.country.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  });
  
  const fieldVerification = phoneVerification[field.id] || { 
    codeSent: false, 
    verified: false,
    error: null 
  };

  const updateVerificationState = (updates) => {
    setPhoneVerification(prev => ({
      ...prev,
      [field.id]: { ...fieldVerification, ...updates }
    }));
  };

  const sendVerificationCode = async () => {
    const fullPhoneNumber = selectedCountry.code + phoneNumber;
    setSendingCode(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/verification/send-phone-code`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'User-Agent': navigator.userAgent
        },
        body: JSON.stringify({ 
          phone: fullPhoneNumber,
          fieldId: field.id 
        })
      });

      if (response.ok) {
        updateVerificationState({ codeSent: true, error: null });
      } else {
        const errorData = await response.json();
        updateVerificationState({ error: errorData.detail || 'Failed to send code' });
      }
    } catch (err) {
      updateVerificationState({ error: 'Network error. Please try again.' });
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async (codeToVerify = verificationCode) => {
    console.log('verifyCode called with:', codeToVerify, 'length:', codeToVerify.length, 'verifyingCode:', verifyingCode);
    if (codeToVerify.length !== 4 || verifyingCode) return;
    
    setVerifyingCode(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/verification/verify-phone-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          fieldId: field.id,
          phone: selectedCountry.code + phoneNumber,
          code: codeToVerify 
        })
      });

      if (response.ok) {
        updateVerificationState({ verified: true, error: null });
        onChange(selectedCountry.code + phoneNumber);
      } else {
        const errorData = await response.json();
        updateVerificationState({ error: errorData.detail || 'Invalid code' });
        // Clear the code on error so user can try again
        setVerificationCode('');
      }
    } catch (err) {
      updateVerificationState({ error: 'Network error. Please try again.' });
      // Clear the code on error so user can try again
      setVerificationCode('');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handlePhoneChange = (newPhoneNumber) => {
    setPhoneNumber(newPhoneNumber);
    if (!field.requireVerification) {
      onChange(selectedCountry.code + newPhoneNumber);
    } else {
      updateVerificationState({ codeSent: false, verified: false, error: null });
    }
  };



  return (
    <div className="mb-8">
      <label className="block text-2xl md:text-3xl font-medium text-gray-900 mb-6 leading-tight">
        {field.label} {field.required && <span className="text-red-500">*</span>}
        {field.requireVerification && (
          <span className="ml-2 text-base text-greay-600 font-normal">(Verification required)</span>
        )}
      </label>
      
      <div className='mb-4'>
            {/* Phone Input Container - Hidden during verification */}
      {!fieldVerification.codeSent && !fieldVerification.verified && (
        <div className="relative w-full">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-0 sm:border-0 sm:border-b-2 border-gray-200 transition-all duration-200">
            {/* Country Selector and Phone Input Row */}
            <div className="flex items-center border-0 border-b-2 sm:border-b-0 border-gray-200 flex-1">
            <div className="relative flex-shrink-0" ref={buttonRef}>
              <button
                type="button"
                className="flex items-center space-x-1 sm:space-x-2 px-0 py-4 hover:bg-gray-50 transition-colors focus:outline-none"
                onClick={() => {
                  if (!showCountryDropdown && buttonRef.current) {
                    const rect = buttonRef.current.getBoundingClientRect();
                    setButtonRect(rect);
                  }
                  setSearchQuery(''); // Clear search when opening
                  setShowCountryDropdown(!showCountryDropdown);
                }}
              >
                <span className="text-lg sm:text-xl" style={{ fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif' }}>{selectedCountry.flag}</span>
                <span className="text-gray-700 font-medium text-base sm:text-lg">{selectedCountry.code}</span>
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Country Dropdown */}
              {showCountryDropdown && (
                <div 
                  className="fixed w-[calc(100vw-2rem)] sm:w-80 max-h-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                  style={{ 
                    zIndex: 9999,
                    top: buttonRect?.bottom + 5,
                    left: Math.max(16, Math.min(buttonRect?.left || 16, window.innerWidth - (window.innerWidth > 640 ? 320 : window.innerWidth - 32)))
                  }}
                >
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search countries..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  {/* Countries List */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country, index) => (
                        <button
                          key={`${country.code}-${country.country}`}
                          type="button"
                          className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            setSelectedCountry(country);
                            setShowCountryDropdown(false);
                            setSearchQuery('');
                            if (!field.requireVerification && phoneNumber) {
                              onChange(country.code + phoneNumber);
                            }
                          }}
                        >
                          <span className="text-lg" style={{ fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif' }}>{country.flag}</span>
                          <span className="text-gray-700 font-medium min-w-12">{country.code}</span>
                          <span className="text-gray-600 text-sm">{country.name}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No countries found matching "{searchQuery}"
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Overlay to close dropdown */}
              {showCountryDropdown && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowCountryDropdown(false)}
                ></div>
              )}
            </div>

            {/* Phone Number Input */}
            
            <input
              type="tel"
              className="flex-1 px-2 sm:px-4 py-4 text-base sm:text-lg border-0 focus:outline-none focus:ring-0 bg-transparent placeholder-gray-400"
              placeholder="Type your phone number..."
              value={phoneNumber}
              onChange={e => handlePhoneChange(e.target.value)}
              required={field.required}
            />
            </div>
            {!fieldVerification.codeSent && (
            <button
                type="button"
                onClick={sendVerificationCode}
                disabled={!phoneNumber || sendingCode}
                className="w-full sm:w-auto inline-flex flex-none items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingCode ? (
                  <>
                    <svg className="animate-spin mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Code'
                )}
              </button>
            
              )}
              
          </div>
        </div>
      )}

      
    </div>
    <div>
      {/* Verification Section */}
      {field.requireVerification && (
        <>
        <div className="flex-auto mt-6">
          {(fieldVerification.codeSent &&!fieldVerification.verified) && (
            <div className="space-y-4 w-full flex flex-col">
              <div className='flex-auto text-center px-2'>
                <div className="w-full flex flex-col sm:flex-row items-center justify-center gap-2 text-xs sm:text-sm text-gray-600">
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-center">A verification code has been sent to {selectedCountry.flag} {selectedCountry.code} {phoneNumber}</span>
              </div>
              </div>
              <div className="flex flex-col items-center space-y-4 px-2">
                {/* 4-Digit Input Boxes */}
                <div className="flex items-center gap-2 sm:gap-3">
                  {[0, 1, 2, 3].map(index => (
                    <input
                      key={index}
                      ref={index === 0 ? (el) => {
                        // Auto-focus first input when code is cleared
                        if (el && verificationCode === '' && fieldVerification.error) {
                          setTimeout(() => el.focus(), 100);
                        }
                      } : null}
                      type="text"
                      maxLength="1"
                      pattern="[0-9]"
                      className="w-12 h-12 sm:w-14 sm:h-14 text-center text-xl sm:text-2xl font-mono border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      value={verificationCode[index] || ''}
                      onChange={e => {
                        const newValue = e.target.value.replace(/\D/g, '');
                        if (newValue.length <= 1) {
                          const newCode = verificationCode.split('');
                          newCode[index] = newValue;
                          const updatedCode = newCode.join('');
                          setVerificationCode(updatedCode);
                          
                          // Auto-focus next input
                          if (newValue && index < 3) {
                            const nextInput = e.target.parentElement.children[index + 1];
                            if (nextInput) nextInput.focus();
                          }
                          
                          // Auto-verify when all 4 digits are entered
                          if (updatedCode.length === 4 && !verifyingCode) {
                            console.log('Auto-verifying code:', updatedCode);
                            setTimeout(() => verifyCode(updatedCode), 100);
                          }
                        }
                      }}
                      onKeyDown={e => {
                        // Handle backspace to move to previous input
                        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
                          const prevInput = e.target.parentElement.children[index - 1];
                          if (prevInput) {
                            prevInput.focus();
                            const newCode = verificationCode.split('');
                            newCode[index - 1] = '';
                            setVerificationCode(newCode.join(''));
                          }
                        }
                      }}
                      onPaste={e => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
                        if (pastedText) {
                          setVerificationCode(pastedText);
                          // Auto-verify if 4 digits pasted
                          if (pastedText.length === 4 && !verifyingCode) {
                            console.log('Auto-verifying pasted code:', pastedText);
                            setTimeout(() => verifyCode(pastedText), 100);
                          }
                        }
                      }}
                    />
                  ))}
                </div>
                
                {/* Loading indicator when verifying */}
                {verifyingCode && (
                  <div className="flex items-center space-x-2 text-gray-600">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Verifying...</span>
                  </div>
                )}
                
                {/* Resend code button */}
                <button
                  type="button"
                  onClick={() => updateVerificationState({ codeSent: false, error: null })}
                  className="text-gray-600 text-sm hover:text-gray-800 transition-colors underline"
                >
                  Resend code
                </button>
              </div>
            </div>
          )}

          {fieldVerification.verified && (
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Phone number verified successfully</span>
            </div>
          )}
          
         
        </div>
        <div className='flex-auto w-full'>
           {fieldVerification.error && (
            <div className="mt-3 flex items-center space-x-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{fieldVerification.error}</span>
            </div>
          )}</div>
          </>
      )}
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-3 flex items-center space-x-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </p>
      )}
    </div>
  );
});

function FormField({ field, value, onChange, error, phoneVerification, setPhoneVerification, buttonColor, themeStyles, isPrefilled }) {
  // A field is locked (read-only) when it was pre-filled from a URL param AND the form editor
  // set prefillLocked = true on that field.
  const isLocked = isPrefilled && field.prefillLocked;

  // Theme-aware input styles
  const getInputStyle = () => {
    if (!themeStyles) return {};
    if (themeStyles.hasBoxBorder) {
      return {
        width: '100%',
        padding: '12px 16px',
        fontSize: themeStyles.fontSize,
        backgroundColor: 'transparent',
        border: `1.5px solid ${error ? '#f87171' : themeStyles.inputBorderColor}`,
        borderRadius: themeStyles.inputBorderRadius,
        outline: 'none',
        color: themeStyles.textColor,
        transition: 'border-color 0.2s',
      };
    }
    return {
      width: '100%',
      padding: '12px 0',
      fontSize: themeStyles.fontSize,
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: `2px solid ${error ? '#f87171' : themeStyles.inputBorderColor}`,
      borderRadius: '0',
      outline: 'none',
      color: themeStyles.textColor,
      transition: 'border-color 0.2s',
    };
  };

  const inputClass = themeStyles ? '' : `w-full px-0 py-4 text-lg bg-transparent border-0 border-b-2 transition-all duration-200 focus:outline-none placeholder-gray-400 ${
    error ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-gray-500'
  }`;

  const labelClass = themeStyles ? '' : "block text-2xl md:text-3xl font-medium text-gray-900 mb-6 leading-tight";
  
  const labelStyle = themeStyles ? {
    display: 'block',
    fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
    fontWeight: '500',
    color: themeStyles.labelColor,
    marginBottom: '20px',
    lineHeight: '1.3',
    fontFamily: themeStyles.headingFontFamily,
  } : undefined;

  const optionRadius = themeStyles ? `${Math.max(parseInt(themeStyles.inputBorderRadius) || 0, 8)}px` : undefined;

  switch (field.type) {
    case 'text':
    case 'email':
    case 'number':
    case 'date':
      return (
        <div className={themeStyles ? '' : 'mb-8'}>
          <label className={labelClass} style={labelStyle}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
            {isLocked && <span className="ml-2 text-xs font-normal opacity-60">(pre-filled)</span>}
          </label>
          <input
            type={field.type}
            className={`${inputClass}${isLocked ? ' opacity-70 cursor-not-allowed' : ''}`}
            style={themeStyles ? { ...getInputStyle(), ...(isLocked ? { opacity: 0.7, cursor: 'not-allowed' } : {}) } : undefined}
            placeholder={field.placeholder || 'Type your answer here...'}
            required={field.required}
            value={value}
            onChange={isLocked ? undefined : e => onChange(e.target.value)}
            readOnly={isLocked}
          />
          {error && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>
      );
    case 'phone':
      return (
        <PhoneField 
          field={field} 
          value={value} 
          onChange={onChange} 
          error={error}
          phoneVerification={phoneVerification}
          setPhoneVerification={setPhoneVerification}
        />
      );
    case 'textarea':
      return (
        <div className={themeStyles ? '' : 'mb-8'}>
          <label className={labelClass} style={labelStyle}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
            {isLocked && <span className="ml-2 text-xs font-normal opacity-60">(pre-filled)</span>}
          </label>
          <textarea
            className={themeStyles ? (isLocked ? 'opacity-70 cursor-not-allowed' : '') : `${inputClass} resize-none${isLocked ? ' opacity-70 cursor-not-allowed' : ''}`}
            style={themeStyles ? { ...getInputStyle(), resize: 'none', ...(isLocked ? { opacity: 0.7, cursor: 'not-allowed' } : {}) } : undefined}
            placeholder={field.placeholder || 'Type your answer here...'}
            required={field.required}
            value={value}
            onChange={isLocked ? undefined : e => onChange(e.target.value)}
            readOnly={isLocked}
            rows={4}
          />
          {error && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>
      );
    case 'radio':
      return (
        <div className={themeStyles ? '' : 'mb-8'}>
          <div className={labelClass} style={labelStyle}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
            {isLocked && <span className="ml-2 text-xs font-normal opacity-60">(pre-filled)</span>}
          </div>
          <div className="space-y-3">
            {(field.options || []).map((option) => (
              <label 
                key={option} 
                className={`flex items-center p-4 border-2 transition-all duration-200${isLocked ? ' cursor-not-allowed opacity-70' : ' cursor-pointer'}`}
                style={{
                  borderColor: value === option ? (buttonColor || '#3B82F6') : (themeStyles?.inputBorderColor || '#e5e7eb'),
                  backgroundColor: value === option ? `${buttonColor || '#3B82F6'}10` : 'transparent',
                  borderRadius: optionRadius || '8px',
                }}
              >
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={isLocked ? undefined : () => onChange(option)}
                  required={field.required}
                  disabled={isLocked}
                  className="mr-4 w-5 h-5 text-gray-600 focus:ring-gray-500 focus:ring-2"
                />
                <span 
                  className={themeStyles ? 'font-medium' : 'text-lg font-medium text-gray-900'}
                  style={themeStyles ? { fontSize: themeStyles.fontSize, color: themeStyles.textColor } : undefined}
                >
                  {option}
                </span>
              </label>
            ))}
          </div>
          {error && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>
      );
    case 'select':
      return (
        <div className={themeStyles ? '' : 'mb-8'}>
          <label className={labelClass} style={labelStyle}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
            {isLocked && <span className="ml-2 text-xs font-normal opacity-60">(pre-filled)</span>}
          </label>
          <select
            className={`${inputClass}${isLocked ? ' opacity-70 cursor-not-allowed' : ''}`}
            style={themeStyles ? { ...getInputStyle(), cursor: isLocked ? 'not-allowed' : 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', ...(isLocked ? { opacity: 0.7 } : {}) } : undefined}
            required={field.required}
            value={value || ''}
            onChange={isLocked ? undefined : e => onChange(e.target.value)}
            disabled={isLocked}
          >
            <option value="">{field.placeholder || 'Select an option...'}</option>
            {(field.options || []).map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
          {error && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>
      );
    case 'checkbox':
      return (
        <div className={themeStyles ? '' : 'mb-8'}>
          <div className={labelClass} style={labelStyle}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
            {isLocked && <span className="ml-2 text-xs font-normal opacity-60">(pre-filled)</span>}
          </div>
          <div className="space-y-3">
            {(field.options || []).map(option => (
              <label 
                key={option}
                className={`flex items-center p-4 border-2 transition-all duration-200${isLocked ? ' cursor-not-allowed opacity-70' : ' cursor-pointer'}`}
                style={{
                  borderColor: Array.isArray(value) && value.includes(option) ? (buttonColor || '#3B82F6') : (themeStyles?.inputBorderColor || '#e5e7eb'),
                  backgroundColor: Array.isArray(value) && value.includes(option) ? `${buttonColor || '#3B82F6'}10` : 'transparent',
                  borderRadius: optionRadius || '8px',
                }}
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={Array.isArray(value) && value.includes(option)}
                  onChange={isLocked ? undefined : e => {
                    let next;
                    if (Array.isArray(value)) {
                      next = e.target.checked
                        ? [...value, option]
                        : value.filter(v => v !== option);
                    } else {
                      next = e.target.checked ? [option] : [];
                    }
                    onChange(next);
                  }}
                  disabled={isLocked}
                  className="mr-4 w-5 h-5 text-blue-600 focus:ring-blue-500 focus:ring-2 rounded"
                />
                <span 
                  className={themeStyles ? 'font-medium' : 'text-lg font-medium text-gray-900'}
                  style={themeStyles ? { fontSize: themeStyles.fontSize, color: themeStyles.textColor } : undefined}
                >
                  {option}
                </span>
              </label>
            ))}
          </div>
          {error && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>
      );
    case 'hidden':
      // Hidden fields should not be displayed to users
      return null;
    case 'heading': {
      const level = field.headingLevel || 'h2';
      const sizeMap = {
        h1: 'clamp(1.75rem, 4vw, 2.5rem)',
        h2: 'clamp(1.4rem, 3vw, 2rem)',
        h3: 'clamp(1.1rem, 2.5vw, 1.5rem)',
      };
      const headingBaseClass = {
        h1: 'text-3xl md:text-4xl font-bold text-gray-900 leading-tight',
        h2: 'text-2xl md:text-3xl font-bold text-gray-900 leading-tight',
        h3: 'text-xl md:text-2xl font-semibold text-gray-900 leading-snug',
      };
      const Tag = level;
      return (
        <Tag
          className={themeStyles ? '' : (headingBaseClass[level] || headingBaseClass.h2)}
          style={themeStyles ? {
            fontSize: sizeMap[level] || sizeMap.h2,
            fontWeight: '700',
            color: themeStyles.textColor,
            lineHeight: '1.2',
            fontFamily: themeStyles.headingFontFamily,
            margin: 0,
          } : undefined}
        >
          {field.content || ''}
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p
          className={themeStyles ? '' : 'text-base md:text-lg text-gray-700 leading-relaxed'}
          style={themeStyles ? {
            fontSize: themeStyles.fontSize,
            color: themeStyles.textColor,
            lineHeight: '1.7',
            fontFamily: themeStyles.fontFamily,
            margin: 0,
            whiteSpace: 'pre-wrap',
          } : { whiteSpace: 'pre-wrap' }}
        >
          {field.content || ''}
        </p>
      );
    case 'image': {
      if (!field.imageUrl) return null;
      const alignMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
      const justifyContent = alignMap[field.alignment || 'center'];
      return (
        <div style={{ display: 'flex', justifyContent }}>
          <img
            src={field.imageUrl}
            alt={field.altText || ''}
            className="max-w-full h-auto"
            style={{ borderRadius: themeStyles?.inputBorderRadius || '0px', display: 'block' }}
          />
        </div>
      );
    }
    default:
      return null;
  }
}
